import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { debounce } from '@/utils';
import { DateRange } from '@/components/common/DateRangePicker';
import {
  // useGetCompetitionByIdQuery,
  // useGetEventsQuery,
  useGetPendingMarketsQuery,
} from '@/store/api/eventApi';
import { MarketRunners, useLazyGetMarketRunnerQuery } from '@/store/api/runnersApi';
import {
  DeclareResultPayload,
  RollbackPayload,
  useDeclareResultMutation,
  useRollbackResultMutation,
} from '@/store/api/betResultApi';
import { PendingMarketItem } from '@/types/pendingMarket';
import { useGetRollbackMarketsQuery } from '@/store/api/eventApi';
import dayjs from 'dayjs';
import { useGetPlatformsQuery } from '@/store/api/platformsApi';
import { useGetBetStatsQuery } from '@/store/api/statsApi';

export type ResultValue = {
  status: 'Winner' | 'Looser' | 'None';
  selectionId: string;
  selectionName?: string;
};

const sportOptions = [
  { label: 'All', value: 'all' },
  { label: 'Cricket', value: 'Cricket' },
  { label: 'Football', value: 'Soccer' },
  { label: 'Tennis', value: 'Tennis' },
];
export type ResultTabType = 'pending' | 'rollback';
type ResultActionType = 'declare' | 'rollback';

export const useEventListing = () => {
  const [isPollingEnabled, setIsPollingEnabled] = useState(true);
  const [sport, setSport] = useState<string>('all');
  const [webhookName, setWebhookName] = useState<string>('all');
  // const [competitionId, setCompetitionId] = useState<number | undefined>(undefined);
  // const [eventId, setEventId] = useState<string | undefined>('all');
  const [activeResultTab, setActiveResultTab] = useState<ResultTabType>('pending');

  // pagination & filters
  const [search, setSearch] = useState('');
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: dayjs().startOf('day').toDate(),
    to: dayjs().endOf('day').toDate(),
  });

  // modal state
  const [open, setOpen] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [runners, setRunners] = useState<MarketRunners>({} as MarketRunners);
  const [eventDetails, setEventDetails] = useState<PendingMarketItem>();

  // APIs

  const { data: betStatsData } = useGetBetStatsQuery(
    {
      ...(dateRange?.from && {
        fromDate: dayjs(dateRange.from).toISOString(),
      }),
      ...(dateRange?.to && {
        toDate: dayjs(dateRange.to).toISOString(),
      }),
      sport: sport !== 'all' ? sport : undefined,
      webhookName: webhookName !== 'all' ? webhookName : undefined,
    },
    {
      pollingInterval: isPollingEnabled ? 5000 : 0,
    }
  );
  const betCounts = {
    totalBets: betStatsData?.stats?.totalBets ?? 0,
    settledBets: betStatsData?.stats?.settledBets ?? 0,
    unsettledBets: betStatsData?.stats?.pendingBets ?? 0, 
    rollbackBets: betStatsData?.stats?.rollbackedBets ?? 0, 
  };

  const { data, isLoading, isFetching } = useGetPendingMarketsQuery(
    {
      page,
      limit,
      search,
      ...(dateRange?.from && {
        fromDate: dayjs(dateRange.from).toISOString(),
      }),
      ...(dateRange?.to && {
        toDate: dayjs(dateRange.to).toISOString(),
      }),
      // eventId: eventId !== 'all' ? Number(eventId) : undefined,
      sport: sport !== 'all' ? sport : undefined,
      webhookName: webhookName !== 'all' ? webhookName : undefined,
    },
    {
      skip: activeResultTab !== 'pending',
      pollingInterval: isPollingEnabled && activeResultTab === 'pending' ? 5000 : 0,
    }
  );

  const { data: webhookData } = useGetPlatformsQuery();

  const {
    data: rollbackData,
    isLoading: rollbackLoading,
    isFetching: rollbackFetching,
  } = useGetRollbackMarketsQuery(
    {
      page,
      limit,
      search,
      ...(dateRange?.from && {
        fromDate: dayjs(dateRange.from).toISOString(),
      }),
      ...(dateRange?.to && {
        toDate: dayjs(dateRange.to).toISOString(),
      }),
      // eventId: eventId !== 'all' ? Number(eventId) : undefined,
      sport: sport !== 'all' ? sport : undefined,
      webhookName: webhookName !== 'all' ? webhookName : undefined,
    },
    {
      skip: activeResultTab !== 'rollback',
      pollingInterval: isPollingEnabled && activeResultTab === 'rollback' ? 10000 : 0,
    }
  );

  const [fetchRunner, { isLoading: runnersLoading }] = useLazyGetMarketRunnerQuery();
  const [declareResult, { isLoading: betResultLoading }] = useDeclareResultMutation();
  const [rollbackResult, { isLoading: rollbackResultLoading }] = useRollbackResultMutation();
  const isActionLoading = betResultLoading || rollbackResultLoading;

  //events api
  // const { data: competitionData, isFetching: competitionLoading } = useGetCompetitionByIdQuery(sport, {
  //   skip: sport === 'all',
  // });
  // const competitionOptions = [
  //   ...(competitionData?.competitions?.map((c) => ({
  //     label: c.name,
  //     value: c.id,
  //   })) || []),
  // ];

  // const { data: eventsData, isFetching: eventsLoading } = useGetEventsQuery(
  //   { competitionId },
  //   {
  //     skip: !competitionId || sport === 'all',
  //   }
  // );
  // const eventOptions = [
  //   ...(eventsData?.events?.map((e) => ({
  //     label: e.name,
  //     value: e.id,
  //   })) || []),
  // ];
  const items = activeResultTab === 'pending' ? data?.markets || [] : rollbackData?.markets || [];

  const totalItems =
    activeResultTab === 'pending'
      ? data?.pagination?.totalItems || 0
      : rollbackData?.pagination?.totalItems || 0;

  // Debounced search
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearch(value);
      setPage(1);
    }, 500)
  ).current;

  // Open Result Modal
  const handleOpenResult = async (row: PendingMarketItem) => {
    try {
      setSelectedEventName(row.eventName);
      setEventDetails(row);

      if (activeResultTab === 'rollback') {
        setRollbackConfirmOpen(true);
        return;
      }

      let runnersData = {} as MarketRunners;

      if (row.marketType?.toLowerCase() === 'fancy') {
        runnersData = {
          id: '',
          externalId: '',
          eventId: '',
          status: '',
          runners: [
            {
              runnerName: row.selection || '',
              handicap: 0,
              selectionId: '',
              sortPriority: '',
              runnerId: '',
            },
          ],
        };
      } else {
        const response = await fetchRunner({
          eventId: row.eventExternalId,
          marketId: row.marketId,
        }).unwrap();

        runnersData = response.marketRunners;
      }

      setRunners(runnersData);
      setOpen(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load. Please try again.');
    }
  };

  // Declare Result
  const handleResultAction = async (
    action: ResultActionType,
    data: Record<string, ResultValue>,
    runs: string,
    options?: { eventDetails?: PendingMarketItem; isCancel?: boolean }
  ): Promise<boolean> => {
    const details = options?.eventDetails ?? eventDetails;
    if (!details) return false;

    const { marketName, marketType, eventExternalId, marketId, resultId } = details as any;

    const winner = Object.values(data).find((r) => r.status === 'Winner');

    const isCancel = options?.isCancel;
    const isFancy = marketType?.toLowerCase() === 'fancy';
    const isSession = marketName?.toLowerCase() === 'session';
    const isNormal =
      marketType?.toLowerCase() === 'normal' || marketName?.toLowerCase() !== 'session';

    let selectionId = '';
    let resultToShow = undefined;

    if (isCancel) {
      selectionId = '-999';
      resultToShow = 'Cancelled';
    } else if (winner?.selectionId) {
      selectionId = winner.selectionId;
      resultToShow = winner.selectionName;
    } else if (isFancy) {
      selectionId = marketId || '';
      resultToShow = runs || '';
    }

    // 👉 Apply loss ONLY for your exact condition
    if (isFancy && !isSession && !isCancel && !winner && selectionId) {
      selectionId = `${selectionId}_loss`;
      resultToShow = 'No';
    }
    // 👉 Apply loss ONLY for your exact condition
    if (isFancy && !isSession && !isCancel && winner && selectionId) {
      selectionId = selectionId;
      resultToShow = 'Yes';
    }

    const result = isCancel ? '-999' : isNormal ? selectionId : runs || '';
    // const resultToShow = isCancel ? 'Cancelled' : isNormal ? winner?.selectionName : undefined;

    const payload: DeclareResultPayload = {
      eventExternalId: eventExternalId || '',
      marketId: marketId || '',
      selectionId,
      resultToShow,
      result,
    };

    const rollbackPayload: RollbackPayload = {
      resultId: resultId,
    };

    try {
      const res =
        action === 'declare'
          ? await declareResult(payload).unwrap()
          : await rollbackResult(rollbackPayload).unwrap();

      toast.success(
        res?.message ||
          (action === 'declare'
            ? 'Result declared successfully!'
            : 'Result rolled back successfully!')
      );

      setOpen(false);
      return true;
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Action failed');
      return false;
    }
  };

  const handleSportChange = (value: string) => {
    setSport(value);
    setPage(1);

    // if (value === 'all') {
    //   setCompetitionId(undefined);
    //   setEventId(undefined);
    // } else {
    //   setCompetitionId(undefined);
    //   setEventId(undefined);
    // }
  };

  // const handleCompetitionChange = (value: number) => {
  //   setCompetitionId(value);
  //   setEventId(undefined);
  // };

  // webookname options
  const webhookNameOptions = [
    { label: 'All', value: 'all' },
    ...(webhookData?.platforms.map((p) => ({
      label: p.name,
      value: String(p.name),
    })) ?? []),
  ];

  const handleWebhookNameChange = (value: string) => {
    setWebhookName(value);
    setPage(1);
  };

  const handleTabChange = (tab: ResultTabType) => {
    setActiveResultTab(tab);
    setPage(1);
  };

  const isTableLoading =
    activeResultTab === 'pending'
      ? isLoading || (isFetching && !isPollingEnabled)
      : rollbackLoading || (rollbackFetching && !isPollingEnabled);

  return {
    // table data
    items,
    totalItems,
    isTableLoading,
    betResultLoading: isActionLoading,
    // runnersLoading,
    // loadingEventId

    //stats
    betCounts,

    // pagination & filters
    page,
    limit,
    setPage,
    setLimit,
    dateRange,
    setDateRange,
    debouncedSearch,
    isPollingEnabled,
    setIsPollingEnabled,

    // modal
    open,
    setOpen,
    rollbackConfirmOpen, 
    setRollbackConfirmOpen, 
    selectedEventName,
    lastResult: (eventDetails as any)?.resultToShow || eventDetails?.result || '-',
    marketNameLabel: eventDetails?.marketName || '', 
    runners,
    marketType: (eventDetails?.marketType || '').toLowerCase(),
    marketName: (eventDetails?.marketName || '').toLowerCase(),

    // actions
    handleOpenResult,
    handleResultAction,

    // FILTERS
    sport,
    // competitionId,
    // eventId,
    sportOptions,
    // competitionOptions,
    // eventOptions,
    handleSportChange,
    // handleCompetitionChange,
    // setEventId,
    // competitionLoading,
    // eventsLoading,
    webhookName,
    webhookNameOptions,
    handleWebhookNameChange,
    activeResultTab,
    handleTabChange,
  };
};
